/**
 * OpenTable Partner Application Configuration
 *
 * Single source of truth for every value that flows into the OpenTable partner
 * application form, the public Brand Compliance Page, the readiness checklist,
 * and the runtime branding scaffolding consumed by `RestaurantCard` and
 * `ReservationBooking`.
 *
 * Spec: .kiro/specs/opentable-partner-application/
 * Task 1.1 — validateConfig + new top-level fields (Req 1.1, 1.2, 1.5, 1.6, 1.7,
 *   4.2, 6.1, 6.4, 6.6, 6.7, 8.1, 8.2, 8.4, 8.5, 11.1, 11.4)
 *
 * Two-step migration: the legacy `partner` / `branding` / `affiliate` / `api`
 * sub-objects are retained so existing consumers (affiliateService,
 * RestaurantCard, ReservationBooking) keep compiling. New work consumes the
 * flat top-level interface.
 */

/**
 * Error thrown when validateConfig encounters an invalid field.
 * Names exactly one offending field per Req 1.7.
 */
export class ConfigValidationError extends Error {
  constructor(field, message) {
    super(`OPENTABLE_CONFIG.${field}: ${message}`);
    this.name = 'ConfigValidationError';
    this.field = field;
  }
}

/**
 * Recursively freeze an object and every nested object/array. Returns the
 * same reference for chaining.
 */
export function deepFreeze(value) {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) {
    return value;
  }
  Object.freeze(value);
  for (const key of Object.keys(value)) {
    deepFreeze(value[key]);
  }
  return value;
}

/**
 * Public, immutable tagline constant. Matches the OpenTable partner agreement
 * exactly (case-sensitive, no whitespace) per Req 4.2.
 */
export const OPENTABLE_TAGLINE = 'Powered by OpenTable';

// ── Validation helpers ───────────────────────────────────────────────────────

const PLACEHOLDER_EMAILS = new Set([
  'example@example.com',
  'test@test.com',
  'you@example.com',
  'changeme@example.com',
  'your-email@domain.com',
]);

const PLACEHOLDER_SUBSTRINGS = ['placeholder', 'todo', 'changeme'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const ISO_COUNTRY_REGEX = /^[A-Z]{2}$/;
const LOGO_PATH_REGEX = /^\/.*opentable.*\.(svg|png|jpg)$/i;

function assertField(condition, field, message) {
  if (!condition) {
    throw new ConfigValidationError(field, message);
  }
}

function isInteger(n, min, max) {
  return Number.isInteger(n) && n >= min && n <= max;
}

function isNonEmptyString(s, min, max) {
  return typeof s === 'string' && s.trim().length >= min && s.length <= max;
}

function isHttpsUrl(s) {
  if (typeof s !== 'string' || !s.startsWith('https://')) return false;
  try {
    const u = new URL(s);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

function isRealCalendarDate(s) {
  if (!ISO_DATE_REGEX.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function isPlaceholderEmail(email) {
  const lc = email.toLowerCase();
  if (PLACEHOLDER_EMAILS.has(lc)) return true;
  const [local, domain] = lc.split('@');
  if (!local || !domain) return false;
  return PLACEHOLDER_SUBSTRINGS.some((p) => local.includes(p) || domain.includes(p));
}

/**
 * Validate every field of a candidate config. Throws ConfigValidationError
 * naming exactly one offending field on the first violation. Returns the
 * config when valid.
 *
 * Pure, total, field-local — designed to support fast-check property tests.
 */
export function validateConfig(c) {
  // Short-form scalars (Req 1.1, 6.1)
  assertField(isNonEmptyString(c.companyName, 1, 200), 'companyName', 'must be 1–200 chars');
  assertField(isHttpsUrl(c.websiteUrl), 'websiteUrl', 'must be an absolute https:// URL');
  assertField(typeof c.operatingCountry === 'string' && ISO_COUNTRY_REGEX.test(c.operatingCountry),
    'operatingCountry', 'must be an ISO 3166-1 alpha-2 country code (e.g. "US")');
  assertField(isInteger(c.yearsInBusiness, 0, 100), 'yearsInBusiness', 'must be an integer 0–100');
  assertField(isNonEmptyString(c.productDescription, 50, 1000), 'productDescription', 'must be 50–1000 chars');
  assertField(isNonEmptyString(c.productType, 1, 100), 'productType', 'must be 1–100 chars');
  assertField(isNonEmptyString(c.industryType, 1, 100), 'industryType', 'must be 1–100 chars');
  assertField(isInteger(c.monthlyActiveUsers, 0, 1_000_000_000), 'monthlyActiveUsers', 'must be an integer 0–1e9');
  assertField(isInteger(c.restaurantUsers, 0, 1_000_000_000), 'restaurantUsers', 'must be an integer 0–1e9');
  assertField(isInteger(c.averageActiveAppUsersPerMonth, 0, 1_000_000_000),
    'averageActiveAppUsersPerMonth', 'must be an integer 0–1e9');
  assertField(isInteger(c.monthlyActiveUsersFallbackNumeric, 0, 1_000_000),
    'monthlyActiveUsersFallbackNumeric', 'must be an integer 0–1e6 (Req 6.6)');

  // desiredFunctionality (Req 1.2)
  assertField(Array.isArray(c.desiredFunctionality) && c.desiredFunctionality.length >= 2,
    'desiredFunctionality', 'must be an array with at least 2 entries');
  c.desiredFunctionality.forEach((entry, i) => {
    assertField(isNonEmptyString(entry, 1, 500),
      `desiredFunctionality[${i}]`, 'each entry must be 1–500 chars');
  });
  const flatFn = c.desiredFunctionality.join(' ').toLowerCase();
  assertField(/(directory|metadata)/.test(flatFn),
    'desiredFunctionality',
    'must include at least one entry referencing the OpenTable Directory API or restaurant metadata');
  assertField(/(reservation|booking)/.test(flatFn),
    'desiredFunctionality',
    'must include at least one entry referencing reservation booking');

  // Partner contact (Req 8.1–8.5, 2.6)
  assertField(typeof c.partnerContactEmail === 'string'
    && c.partnerContactEmail.length >= 5
    && c.partnerContactEmail.length <= 254
    && EMAIL_REGEX.test(c.partnerContactEmail),
    'partnerContactEmail', 'must be a valid email of 5–254 chars');
  assertField(!isPlaceholderEmail(c.partnerContactEmail),
    'partnerContactEmail', 'must not match a known placeholder pattern (Req 8.2)');
  assertField(isNonEmptyString(c.partnerContactName, 1, 100)
    && c.partnerContactName.trim().length > 0,
    'partnerContactName', 'must be 1–100 chars with at least one non-whitespace character');

  // Branding (Req 4.2, legacy + freeze constraints)
  assertField(c.branding && typeof c.branding === 'object', 'branding', 'must be an object');
  assertField(c.branding.tagline === OPENTABLE_TAGLINE,
    'branding.tagline', `must be exactly "${OPENTABLE_TAGLINE}" (Req 4.2)`);
  assertField(typeof c.branding.logoPath === 'string' && LOGO_PATH_REGEX.test(c.branding.logoPath),
    'branding.logoPath', 'must be a root-relative path containing "opentable" with extension .svg/.png/.jpg');
  assertField(typeof c.branding.logoFullPath === 'string' && LOGO_PATH_REGEX.test(c.branding.logoFullPath),
    'branding.logoFullPath', 'must be a root-relative path containing "opentable" with extension .svg/.png/.jpg');
  assertField(isInteger(c.branding.minTaglineFontSize, 11, 200),
    'branding.minTaglineFontSize', 'must be an integer ≥ 11 (Req 9.3)');
  assertField(isInteger(c.branding.logoSize, 1, 1024),
    'branding.logoSize', 'must be a positive integer ≤ 1024');

  // Brand Compliance Page (Req 11.1, 11.4)
  assertField(isHttpsUrl(c.brandCompliancePageUrl),
    'brandCompliancePageUrl', 'must be an absolute https:// URL');
  try {
    const u = new URL(c.brandCompliancePageUrl);
    assertField(u.pathname && u.pathname !== '/',
      'brandCompliancePageUrl', 'must include a non-empty path');
  } catch {
    /* unreachable — covered by isHttpsUrl */
  }

  // Long-form answers pointer (Req 6.4 — existence is enforced by the
  // readiness checklist's longFormAnswersGate, not module load)
  assertField(isNonEmptyString(c.longFormAnswersPath, 1, 500),
    'longFormAnswersPath', 'must be a non-empty relative path string');

  // Audit field (Req 1.6)
  assertField(typeof c.lastReviewedAt === 'string' && isRealCalendarDate(c.lastReviewedAt),
    'lastReviewedAt', 'must be an ISO 8601 calendar date YYYY-MM-DD');

  return { valid: true };
}

// ── Authoritative configuration ──────────────────────────────────────────────

const RAW_CONFIG = {
  /** OpenTable form: "Company Name" — short text, 1–200 chars (Req 1.1) */
  companyName: 'TravelFlow',

  /** OpenTable form: "Website URL" — absolute https:// URL (Req 1.1) */
  websiteUrl: 'https://travelflow.link',

  /** OpenTable form: "Country of operation" — ISO 3166-1 alpha-2 (Req 1.1) */
  operatingCountry: 'US',

  /** OpenTable form: "Years in business" — integer 0–100 (Req 1.1) */
  yearsInBusiness: 1,

  /** OpenTable form: "Product description" — 50–1000 chars (Req 1.1) */
  productDescription:
    'TravelFlow is an AI-powered journey orchestrator that models the entire travel '
    + 'experience as a single stateful timeline and surfaces the next relevant action '
    + 'at any moment, including AI-driven dining recommendations near each trip stop.',

  /** OpenTable form: "Product type" — short text 1–100 chars (Req 1.1) */
  productType: 'Travel itinerary management web and mobile application',

  /** OpenTable form: "Industry type" — short text 1–100 chars (Req 1.1) */
  industryType: 'Travel & Hospitality',

  /** OpenTable form: "Monthly Active Users" — integer 0..1e9 (Req 1.1) */
  monthlyActiveUsers: 0,

  /** OpenTable form: "Restaurant users" — integer 0..1e9 (Req 1.1) */
  restaurantUsers: 0,

  /** OpenTable form: "Average active app users per month" — integer 0..1e9 (Req 1.1) */
  averageActiveAppUsersPerMonth: 0,

  /**
   * OpenTable form: "Monthly Active Users (numeric)" fallback when the form
   * rejects a free-text answer — integer 0..1e6 (Req 6.6).
   */
  monthlyActiveUsersFallbackNumeric: 0,

  /**
   * OpenTable form: "Desired functionality" — non-empty array, each entry
   * 1–500 chars; must mention metadata + booking (Req 1.2).
   */
  desiredFunctionality: [
    'Consume restaurant metadata (name, cuisine, price range, neighborhood, '
    + 'photos) via the OpenTable Directory API to enrich AI-driven dining '
    + 'recommendations within TravelFlow trip itineraries.',
    'Generate affiliate-attributed reservation booking links so travelers can '
    + 'reserve a table directly from a TravelFlow restaurant card with proper '
    + '"Powered by OpenTable" branding.',
  ],

  /** OpenTable form: "Partner contact name" — 1–100 chars (Req 8.4–8.5) */
  partnerContactName: 'Danny Milliorn',

  /**
   * OpenTable form: "Partner contact email" — valid email, not a placeholder
   * (Req 2.6, 8.1–8.3).
   */
  partnerContactEmail: 'admin@travelflow.link',

  /**
   * OpenTable form: "Branding compliance page" — absolute https:// URL of
   * the public Brand Compliance Page (Req 11.1, 11.4).
   */
  brandCompliancePageUrl: 'https://travelflow.link/opentable-branding.html',

  /**
   * Pointer to the prose long-form answers doc, resolved relative to this
   * config file. Existence is enforced by the readiness checklist's
   * longFormAnswersGate (Req 6.4–6.5).
   */
  longFormAnswersPath: '../../../docs/opentable-partner-application.md',

  /**
   * Calendar date the prepared answers were last reviewed for accuracy
   * — ISO 8601 YYYY-MM-DD (Req 1.6).
   */
  lastReviewedAt: '2026-04-21',

  // ── Legacy nested sub-objects (back-compat — design's two-step migration) ──

  /** Legacy: consumed by older code paths; mirrors the flat top-level fields. */
  partner: {
    companyName: 'TravelFlow',
    websiteUrl: 'https://travelflow.link',
    operatingCountry: 'United States',
    yearsInBusiness: 1,
    productDescription: 'AI-powered travel management app with dining recommendations',
    productType: 'Mobile/Web Application',
    industryType: 'Travel & Hospitality',
    monthlyActiveUsers: 0,
    restaurantUsers: 0,
    averageActiveAppUsers: 0,
  },

  /** Affiliate tracking — partnerId remains empty until OpenTable approval. */
  affiliate: {
    partnerId: '',
    commissionRate: 0.04,
  },

  /**
   * OpenTable branding scaffolding — locally bundled assets, exact tagline.
   * The flat top-level OPENTABLE_TAGLINE constant is the canonical reference;
   * this nested copy preserves the legacy interface for existing consumers.
   */
  branding: {
    logoPath: '/assets/opentable-logo.svg',
    logoFullPath: '/assets/opentable-logo-full.svg',
    tagline: OPENTABLE_TAGLINE,
    minTaglineFontSize: 11,
    logoSize: 24,
  },

  /** API connection settings — carried forward; runtime calls live elsewhere. */
  api: {
    baseUrl: 'https://platform.opentable.com/sync/v2',
    timeoutMs: 10000,
    cacheTtlHours: 24,
  },
};

// Module-load validation — fails the Webpack bundle compile on bad config
// (Req 1.7). No partial-config fallback.
validateConfig(RAW_CONFIG);

/**
 * Frozen, validated OpenTable partner-application configuration.
 * @type {Readonly<typeof RAW_CONFIG>}
 */
export const OPENTABLE_CONFIG = deepFreeze(RAW_CONFIG);
